import React, { useState } from 'react';
import { Ban, Loader2, PhoneForwarded } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const api = async (path, opts = {}) => {
  const r = await fetch(`${API_URL}/api${path}`, { credentials: 'include', headers: { 'Content-Type': 'application/json' }, ...opts });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || 'Request failed');
  return d;
};
const today = () => new Date().toISOString().slice(0, 10);
// Local (UK browser) date + time → ISO
const toIso = (date, time) => new Date(`${date}T${time}:00`).toISOString();

export function AddCallbackDialog({ open, onClose, onDone, slotMinutes = 15 }) {
  const [f, setF] = useState({ name: '', phone: '', email: '', date: today(), time: '11:00', duration_minutes: slotMinutes, notes: '', notify: true });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((o) => ({ ...o, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const save = async () => {
    if (!f.name.trim()) { toast.error('Name is required'); return; }
    setBusy(true);
    try {
      await api('/advisor/bookings', { method: 'POST', body: JSON.stringify({ name: f.name, phone: f.phone, email: f.email, start: toIso(f.date, f.time), duration_minutes: Number(f.duration_minutes), notes: f.notes, notify: f.notify }) });
      toast.success('Callback added to the diary'); onDone(); onClose();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="adv-add-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><PhoneForwarded className="w-5 h-5 text-[#1f4030]" /> Book a callback</DialogTitle></DialogHeader>
        <div className="space-y-2 text-sm">
          <Input placeholder="Person's name *" value={f.name} onChange={set('name')} data-testid="adv-add-name" />
          <div className="grid grid-cols-2 gap-2"><Input placeholder="Mobile" value={f.phone} onChange={set('phone')} data-testid="adv-add-phone" /><Input placeholder="Email" value={f.email} onChange={set('email')} data-testid="adv-add-email" /></div>
          <div className="grid grid-cols-3 gap-2">
            <label className="text-xs">Date<Input type="date" value={f.date} onChange={set('date')} data-testid="adv-add-date" /></label>
            <label className="text-xs">Time<Input type="time" value={f.time} onChange={set('time')} data-testid="adv-add-time" /></label>
            <label className="text-xs">Length (min)<Input type="number" min="5" value={f.duration_minutes} onChange={set('duration_minutes')} data-testid="adv-add-length" /></label>
          </div>
          <Textarea rows={2} placeholder="Notes (what it's about)" value={f.notes} onChange={set('notes')} data-testid="adv-add-notes" />
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={f.notify} onChange={set('notify')} data-testid="adv-add-notify" /> Send the person a confirmation text/email (and reminders)</label>
          <p className="text-[11px] text-gray-500">Can be any time, including outside booking-page hours. The slot is blocked on the public page.</p>
        </div>
        <div className="flex justify-end gap-2 pt-2"><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={busy} className="bg-[#1f4030] text-white" data-testid="adv-add-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add to diary'}</Button></div>
      </DialogContent>
    </Dialog>
  );
}

export function BlockTimeDialog({ open, onClose, onDone }) {
  const [f, setF] = useState({ date: today(), allDay: false, from: '11:00', to: '13:00', toDate: '', reason: '' });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((o) => ({ ...o, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const save = async () => {
    setBusy(true);
    try {
      const start = f.allDay ? toIso(f.date, '00:00') : toIso(f.date, f.from);
      const end = f.allDay ? toIso(f.toDate || f.date, '23:59') : toIso(f.date, f.to);
      await api('/advisor/blocks', { method: 'POST', body: JSON.stringify({ start, end, reason: f.reason }) });
      toast.success('Time blocked out'); onDone(); onClose();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="adv-block-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Ban className="w-5 h-5 text-rose-700" /> Block out time</DialogTitle></DialogHeader>
        <div className="space-y-2 text-sm">
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={f.allDay} onChange={set('allDay')} data-testid="adv-block-allday" /> Whole day(s) — e.g. holiday</label>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs">{f.allDay ? 'From date' : 'Date'}<Input type="date" value={f.date} onChange={set('date')} data-testid="adv-block-date" /></label>
            {f.allDay ? <label className="text-xs">To date (optional)<Input type="date" value={f.toDate} onChange={set('toDate')} data-testid="adv-block-todate" /></label>
              : <div className="grid grid-cols-2 gap-2"><label className="text-xs">From<Input type="time" value={f.from} onChange={set('from')} data-testid="adv-block-from" /></label><label className="text-xs">To<Input type="time" value={f.to} onChange={set('to')} data-testid="adv-block-to" /></label></div>}
          </div>
          <Input placeholder="Reason (e.g. Holiday, Training day)" value={f.reason} onChange={set('reason')} data-testid="adv-block-reason" />
          <p className="text-[11px] text-gray-500">Nobody can book during blocked time on the public page.</p>
        </div>
        <div className="flex justify-end gap-2 pt-2"><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={busy} className="bg-rose-700 text-white" data-testid="adv-block-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Block it out'}</Button></div>
      </DialogContent>
    </Dialog>
  );
}
